import { 
  collection, 
  getDocs, 
  query, 
  writeBatch,
  arrayRemove,
  arrayUnion
} from 'firebase/firestore';
import { db } from './firebase';
import { Person } from '../types';

const PEOPLE_COLLECTION = 'people';

// All skill fields from the Person interface
const SKILL_FIELDS = [
  'verktoyOgSystemer',
  'spesialiserteVerktoyUtvikler',
  'spesialiserteVerktoyDesigner',
  'spesialiserteVerktoyMarkedsforing',
  'spesialiserteVerktoyProsjektledelse',
  'spesialiserteVerktoy3D',
  'spesialiserteVerktoyAI'
];

// Human-readable names for skill categories
const SKILL_CATEGORY_NAMES: Record<string, string> = {
  'verktoyOgSystemer': 'Tools & Systems',
  'spesialiserteVerktoyUtvikler': 'Developer Tools',
  'spesialiserteVerktoyDesigner': 'Design Tools',
  'spesialiserteVerktoyMarkedsforing': 'Marketing Tools',
  'spesialiserteVerktoyProsjektledelse': 'Project Management',
  'spesialiserteVerktoy3D': '3D & Animation',
  'spesialiserteVerktoyAI': 'AI Tools'
};

export interface SkillWithCounts {
  skill: string;
  totalCount: number;
  categoryBreakdown: Record<string, number>;
  peopleWithSkill: string[]; // Person names who have this skill
}

// Helper function to check if two skills are similar
function isSimilar(skill1: string, skill2: string): boolean {
  const normalize = (str: string) => str.toLowerCase().trim();
  const norm1 = normalize(skill1);
  const norm2 = normalize(skill2);

  // Exact match after normalization
  if (norm1 === norm2) return true;

  // One is a substring of the other
  if (norm1.includes(norm2) || norm2.includes(norm1)) return true;

  // Similar with common variations (plurals, etc.)
  const variations = [
    [norm1, norm1 + 's'],
    [norm1, norm1 + 'es'],
    [norm1, norm1 + 'ing'],
    [norm2, norm2 + 's'],
    [norm2, norm2 + 'es'],
    [norm2, norm2 + 'ing']
  ];

  for (const [base, variant] of variations) {
    if (base === normalize(skill2) || variant === normalize(skill2)) return true;
    if (base === normalize(skill1) || variant === normalize(skill1)) return true;
  }

  // Levenshtein distance for typos (simple version)
  if (levenshteinDistance(norm1, norm2) <= 2 && Math.min(norm1.length, norm2.length) >= 4) {
    return true;
  }

  return false;
}

// Simple Levenshtein distance calculation
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = [];
  const len1 = str1.length;
  const len2 = str2.length;

  for (let i = 0; i <= len2; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len1; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len2; i++) {
    for (let j = 1; j <= len1; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[len2][len1];
}

export const skillsService = {
  // Get all skills with usage counts across all categories
  async getAllSkillsWithCounts(): Promise<SkillWithCounts[]> {
    try {
      const skillCounts = new Map<string, { 
        totalCount: number; 
        categoryBreakdown: Record<string, number>;
        peopleWithSkill: Set<string>;
      }>();

      // Get all people
      const peopleSnapshot = await getDocs(collection(db, PEOPLE_COLLECTION));
      
      peopleSnapshot.docs.forEach(doc => {
        const person = doc.data() as Person;
        
        // Process each skill category
        SKILL_FIELDS.forEach(fieldName => {
          const skills = person[fieldName as keyof Person] as string[] || [];
          
          skills.forEach((skill: string) => {
            if (skill.trim()) {
              const current = skillCounts.get(skill) || { 
                totalCount: 0, 
                categoryBreakdown: {},
                peopleWithSkill: new Set()
              };
              
              current.totalCount += 1;
              current.categoryBreakdown[fieldName] = (current.categoryBreakdown[fieldName] || 0) + 1;
              current.peopleWithSkill.add(person.name);
              
              skillCounts.set(skill, current);
            }
          });
        });
      });

      // Convert to array and sort by total usage
      return Array.from(skillCounts.entries())
        .map(([skill, data]) => ({
          skill,
          totalCount: data.totalCount,
          categoryBreakdown: data.categoryBreakdown,
          peopleWithSkill: Array.from(data.peopleWithSkill)
        }))
        .sort((a, b) => b.totalCount - a.totalCount);
    } catch (error) {
      console.error('Error fetching skills with counts:', error);
      throw error;
    }
  },

  // Replace a skill across all categories and people
  async replaceSkill(oldSkill: string, newSkill: string, updatedBy: string): Promise<{ updated: number; errors: string[] }> {
    const batch = writeBatch(db);
    const errors: string[] = [];
    let updated = 0;

    try {
      // Get all people
      const peopleSnapshot = await getDocs(collection(db, PEOPLE_COLLECTION));
      
      peopleSnapshot.docs.forEach(doc => {
        const person = doc.data() as Person;
        const personRef = doc.ref;
        let personUpdated = false;

        // Check each skill field
        SKILL_FIELDS.forEach(fieldName => {
          const skills = person[fieldName as keyof Person] as string[] || [];
          
          if (skills.includes(oldSkill)) {
            // Remove old skill
            batch.update(personRef, {
              [fieldName]: arrayRemove(oldSkill)
            });
            
            // Add new skill if it's different and not empty
            if (newSkill.trim() && newSkill !== oldSkill && !skills.includes(newSkill.trim())) {
              batch.update(personRef, {
                [fieldName]: arrayUnion(newSkill.trim())
              });
            }
            
            personUpdated = true;
          }
        });

        if (personUpdated) {
          // Update the updatedAt timestamp
          batch.update(personRef, {
            updatedAt: new Date()
          });
          updated++;
        }
      });

      // Commit all updates
      await batch.commit();

      return { updated, errors };
    } catch (error) {
      console.error('Error replacing skill:', error);
      throw error;
    }
  },

  // Delete a skill (remove from all categories and people)
  async deleteSkill(skillToDelete: string, updatedBy: string): Promise<{ updated: number; errors: string[] }> {
    return this.replaceSkill(skillToDelete, '', updatedBy);
  },

  // Merge multiple skills into one
  async mergeSkills(skillsToMerge: string[], targetSkill: string, updatedBy: string): Promise<{ updated: number; errors: string[] }> {
    let totalUpdated = 0;
    const allErrors: string[] = [];

    try {
      for (const skill of skillsToMerge) {
        if (skill !== targetSkill) {
          const result = await this.replaceSkill(skill, targetSkill, updatedBy);
          totalUpdated += result.updated;
          allErrors.push(...result.errors);
        }
      }

      return { updated: totalUpdated, errors: allErrors };
    } catch (error) {
      console.error('Error merging skills:', error);
      throw error;
    }
  },

  // Get similar skills (for suggesting merges)
  async findSimilarSkills(skills: SkillWithCounts[]): Promise<Array<{ baseSkill: string; similarSkills: string[] }>> {
    const similarGroups: Array<{ baseSkill: string; similarSkills: string[] }> = [];
    const processed = new Set<string>();

    skills.forEach(({ skill }) => {
      if (processed.has(skill)) return;

      const similar = skills
        .filter(({ skill: otherSkill }) => 
          otherSkill !== skill && 
          !processed.has(otherSkill) &&
          isSimilar(skill, otherSkill)
        )
        .map(({ skill: similarSkill }) => {
          processed.add(similarSkill);
          return similarSkill;
        });

      if (similar.length > 0) {
        similarGroups.push({
          baseSkill: skill,
          similarSkills: similar
        });
        processed.add(skill);
      }
    });

    return similarGroups;
  },

  // Get human-readable category name
  getCategoryDisplayName(fieldName: string): string {
    return SKILL_CATEGORY_NAMES[fieldName] || fieldName;
  },

  // Get all skill categories
  getSkillCategories(): Array<{ key: string; name: string }> {
    return SKILL_FIELDS.map(key => ({
      key,
      name: this.getCategoryDisplayName(key)
    }));
  }
};